import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { IDog, IDogClubRegistration, IDogProposedChanges } from "@eagerdog/interfaces";
import { ckcObedienceHeights, ckcRallyHeights, dogAgilityHeights, dogHeightTypes, dogObedienceHeights, dogRallyHeights } from "src/services/helper.service";

import { RegistrationContext, RegistrationField } from "./types";
import { isPalRegistrationType, registrationTypeOptionsFor } from "./registration-types";

const jumpHeightOptions = (options: { id: string; value: string }[]) =>
  options.filter((option) => option.id !== "None");

export const cloneDog = (dog: IDog): IDog => ({
  ...dog,
  club_registrations: dog.club_registrations?.map((registration) => ({ ...registration })),
});

export const mergeProposedClubRegistrations = (dog: IDog, proposed: IDogProposedChanges): IDogClubRegistration[] => {
  const base = dog.club_registrations ?? [];
  const changes = proposed.club_registrations ?? [];
  const map = new Map<string, IDogClubRegistration>(base.map((registration) => [registration.sanctioning_club, { ...registration }]));
  changes.forEach((change) => {
    if (change?.new?.sanctioning_club) {
      map.set(change.new.sanctioning_club, { ...change.new });
    }
  });
  return Array.from(map.values());
};

/** Dog as the registration checks see it: proposed changes for this event overlaid on the saved dog. */
export const effectiveDog = (ctx: RegistrationContext): IDog => {
  const proposed = ctx.proposedChanges;
  if (!proposed) {
    return cloneDog(ctx.dog);
  }

  return {
    ...cloneDog(ctx.dog),
    club_registrations: mergeProposedClubRegistrations(ctx.dog, proposed),
    dob: proposed.dob?.new || ctx.dog.dob,
    sex: proposed.sex?.new || ctx.dog.sex,
    weight: proposed.weight?.new || ctx.dog.weight,
    height_at_withers: proposed.height_at_withers?.new ?? ctx.dog.height_at_withers,
    final_response: proposed.final_response?.new || ctx.dog.final_response,
    country_of_birth: proposed.country_of_birth?.new || ctx.dog.country_of_birth,
    breeder: proposed.breeder?.new || ctx.dog.breeder,
    sire: proposed.sire?.new || ctx.dog.sire,
    dam: proposed.dam?.new || ctx.dog.dam,
    call_name: proposed.call_name?.new || ctx.dog.call_name,
    breed: proposed.breed?.new || ctx.dog.breed,
  };
};

const clubRegistration = (dog: IDog, club: string) =>
  dog.club_registrations?.find((registration) => registration.sanctioning_club === club);

const textMissing = (value?: string | null) => !value || value.trim().length === 0;

const numberMissing = (value?: number | null) => value === undefined || value === null || value <= 0;

export const hasPalNumber = (ctx: RegistrationContext) => {
  const akc = clubRegistration(effectiveDog(ctx), Constants.sanctioning_club.AKC);
  return isPalRegistrationType(akc?.registration_type);
};

export const isUkcConformationPuppy = (ctx: RegistrationContext) => {
  if (ctx.sanctioningClub !== Constants.sanctioning_club.UKC || ctx.show.show_type !== Constants.show_type.conformation) {
    return false;
  }
  const dob = effectiveDog(ctx).dob;
  if (!dob || !ctx.show.show_date) {
    return false;
  }
  const months = moment(ctx.show.show_date).diff(moment(dob), "months");
  return months >= 3 && months < 6;
};

/** A dog entering on a Canine Companion Number (mixed or unrecognized breed) has no CKC pedigree on file. */
export const isCkcCcnDog = (ctx: RegistrationContext) => {
  const ckc = clubRegistration(effectiveDog(ctx), Constants.sanctioning_club.CKC);
  return ckc?.registration_type === Constants.registration_number_type.ckc_ccn;
};

export const isAkcFunMatchExempt = (ctx: RegistrationContext) => {
  return ctx.show.is_fun_match === true
    && ctx.sanctioningClub === Constants.sanctioning_club.AKC
    && (ctx.show.show_type === Constants.show_type.scentwork || ctx.show.show_type === Constants.show_type.conformation);
};

const normalizeAkcRegistrationNumber = (registrationNumber?: string) => {
  if (!registrationNumber) return "";
  return registrationNumber.toUpperCase().replace(/\//g, "");
};

const upsertClub = (dog: IDog, club: string, patch: Partial<IDogClubRegistration>): IDog => {
  const next = cloneDog(dog);
  const registrations = next.club_registrations ? [...next.club_registrations] : [];
  const index = registrations.findIndex((registration) => registration.sanctioning_club === club);
  const current = index === -1 ? undefined : registrations[index];
  const merged: IDogClubRegistration = {
    sanctioning_club: club,
    call_name: "",
    breed: "",
    registration_type: "",
    registration_number: "",
    ...current,
    ...patch,
  };
  merged.call_name = merged.call_name || dog.call_name || "";
  merged.breed = merged.breed || dog.breed || "";
  // Proposed-change validation rejects a missing registered_name. An empty string is allowed.
  merged.registered_name = merged.registered_name || dog.registered_name || "";
  if (index === -1) {
    registrations.push(merged);
  } else {
    registrations[index] = merged;
  }
  next.club_registrations = registrations;
  return next;
};

const heightAtWithersField: RegistrationField = {
  id: "height_at_withers",
  label: "Height at withers (inches)",
  input: { kind: "number", min: 1, step: "0.01" },
  isMissing: (ctx) => numberMissing(effectiveDog(ctx).height_at_withers),
  read: (ctx) => effectiveDog(ctx).height_at_withers,
  apply: (dog, value) => {
    const height = typeof value === "number" ? value : parseFloat(String(value));
    return { ...cloneDog(dog), height_at_withers: Number.isNaN(height) ? undefined : height };
  },
};

const dobField: RegistrationField = {
  id: "dob",
  label: "Date of birth",
  input: { kind: "date" },
  isMissing: (ctx) => !effectiveDog(ctx).dob,
  read: (ctx) => effectiveDog(ctx).dob,
  apply: (dog, value) => {
    const date = value instanceof Date ? value : new Date(String(value));
    return { ...cloneDog(dog), dob: date };
  },
};

const pedigreeField = (id: "sire" | "dam" | "breeder", label: string): RegistrationField => ({
  id,
  label,
  input: { kind: "text" },
  isMissing: (ctx) => {
    if (hasPalNumber(ctx)) return false;
    return textMissing(effectiveDog(ctx)[id]);
  },
  read: (ctx) => effectiveDog(ctx)[id] || "",
  apply: (dog, value) => ({ ...cloneDog(dog), [id]: String(value ?? "").trim() }),
});

// CKC conformation: "Place of Birth" on the entry form and eligibility for Canadian Bred (12.2.1(e)).
const countryOfBirthField: RegistrationField = {
  id: "country_of_birth",
  label: "Country of birth",
  input: { kind: "text" },
  isMissing: (ctx) => textMissing(effectiveDog(ctx).country_of_birth),
  read: (ctx) => effectiveDog(ctx).country_of_birth || "",
  apply: (dog, value) => ({ ...cloneDog(dog), country_of_birth: String(value ?? "").trim() }),
};

const finalResponseField: RegistrationField = {
  id: "final_response",
  label: "Final response",
  input: { kind: "text" },
  isMissing: (ctx) => textMissing(effectiveDog(ctx).final_response),
  read: (ctx) => effectiveDog(ctx).final_response || "",
  apply: (dog, value) => ({ ...cloneDog(dog), final_response: String(value ?? "").trim() }),
};

const clubTextField = (
  id: string,
  label: string,
  club: string,
  key: "call_name" | "breed",
  input: RegistrationField["input"],
): RegistrationField => ({
  id,
  label,
  input,
  isMissing: (ctx) => textMissing(clubRegistration(effectiveDog(ctx), club)?.[key]),
  read: (ctx) => clubRegistration(effectiveDog(ctx), club)?.[key] || "",
  apply: (dog, value) => upsertClub(dog, club, { [key]: String(value ?? "").trim() }),
});

const jumpHeightField = (
  id: string,
  label: string,
  club: string,
  key: keyof IDogClubRegistration,
  options: { id: string; value: string }[],
  legacy?: (dog: IDog) => number | undefined,
): RegistrationField => ({
  id,
  label,
  input: { kind: "select", options: jumpHeightOptions(options) },
  isMissing: (ctx) => {
    const fromClub = clubRegistration(effectiveDog(ctx), club)?.[key] as number | undefined;
    const value = fromClub ?? legacy?.(ctx.dog);
    return numberMissing(value);
  },
  read: (ctx) => {
    const fromClub = clubRegistration(effectiveDog(ctx), club)?.[key] as number | undefined;
    return (fromClub ?? legacy?.(ctx.dog) ?? "").toString();
  },
  apply: (dog, value) => upsertClub(dog, club, { [key]: parseInt(String(value), 10) } as Partial<IDogClubRegistration>),
});

export const registrationField: RegistrationField = {
  id: "registration",
  label: "Registration",
  input: { kind: "registration" },
  isMissing: (ctx) => {
    const registration = clubRegistration(effectiveDog(ctx), ctx.sanctioningClub);
    if (textMissing(registration?.registration_type) || textMissing(registration?.registration_number)) {
      return true;
    }
    // The registration type on file must be one the show accepts (e.g. CKC conformation
    // takes CKC/ERN/MSC/TCN; CCN and PEN are performance-only numbers).
    const accepted = registrationTypeOptionsFor(ctx.sanctioningClub, ctx.show.show_type);
    return accepted.length > 0 && !accepted.some((option) => option.id === registration?.registration_type);
  },
  read: (ctx) => {
    const registration = clubRegistration(effectiveDog(ctx), ctx.sanctioningClub);
    return {
      registration_type: registration?.registration_type || "",
      registration_number: registration?.registration_number || "",
    };
  },
  apply: (dog, value, ctx) => {
    const entered = value as { registration_type?: string; registration_number?: string };
    const number = ctx.sanctioningClub === Constants.sanctioning_club.AKC
      ? normalizeAkcRegistrationNumber(entered.registration_number)
      : (entered.registration_number || "").trim();
    return upsertClub(dog, ctx.sanctioningClub, {
      registration_type: entered.registration_type || "",
      registration_number: number,
    });
  },
};

const ukcJumpHeightTypeField: RegistrationField = {
  id: "ukc_jump_height_type",
  label: "UKC jump height type",
  input: { kind: "select", options: jumpHeightOptions(dogHeightTypes) },
  isMissing: (ctx) => {
    const fromClub = clubRegistration(effectiveDog(ctx), Constants.sanctioning_club.UKC)?.ukc_jump_height_type;
    const value = fromClub || ctx.dog.jump_height_type;
    return !value || value === "None";
  },
  read: (ctx) => {
    const fromClub = clubRegistration(effectiveDog(ctx), Constants.sanctioning_club.UKC)?.ukc_jump_height_type;
    return fromClub || ctx.dog.jump_height_type || "";
  },
  apply: (dog, value) => upsertClub(dog, Constants.sanctioning_club.UKC, { ukc_jump_height_type: String(value) }),
};

export const fields = {
  dob: dobField,
  heightAtWithers: heightAtWithersField,
  sire: pedigreeField("sire", "Sire"),
  dam: pedigreeField("dam", "Dam"),
  breeder: pedigreeField("breeder", "Breeder"),
  finalResponse: finalResponseField,
  countryOfBirth: countryOfBirthField,
  registration: registrationField,
  ukcJumpHeightType: ukcJumpHeightTypeField,
  fhCallName: clubTextField("fh_call_name", "4-H call name", Constants.sanctioning_club.FH, "call_name", { kind: "text" }),
  fhBreed: clubTextField("fh_breed", "4-H breed", Constants.sanctioning_club.FH, "breed", { kind: "breed", sanctioningClub: Constants.sanctioning_club.AKC }),
  generalCallName: clubTextField("general_call_name", "Call name", Constants.sanctioning_club.GENERAL, "call_name", { kind: "text" }),
  generalBreed: clubTextField("general_breed", "Breed", Constants.sanctioning_club.GENERAL, "breed", { kind: "breed", sanctioningClub: Constants.sanctioning_club.UKC }),
  akcObedienceJumpHeight: jumpHeightField(
    "akc_obedience_jump_height",
    "Obedience jump height",
    Constants.sanctioning_club.AKC,
    "akc_obedience_jump_height",
    dogObedienceHeights,
    (dog) => dog.obedience_jump_height,
  ),
  akcPreferredJumpHeight: jumpHeightField(
    "akc_obedience_preferred_jump_height",
    "Obedience preferred jump height",
    Constants.sanctioning_club.AKC,
    "akc_obedience_preferred_jump_height",
    dogObedienceHeights,
  ),
  akcRallyJumpHeight: jumpHeightField(
    "akc_rally_jump_height",
    "Rally jump height",
    Constants.sanctioning_club.AKC,
    "akc_rally_jump_height",
    dogRallyHeights,
    (dog) => dog.rally_jump_height,
  ),
  akcAgilityJumpHeight: jumpHeightField(
    "akc_agility_jump_height",
    "Agility jump height",
    Constants.sanctioning_club.AKC,
    "akc_agility_jump_height",
    dogAgilityHeights,
  ),
  ckcObedienceJumpHeight: jumpHeightField(
    "ckc_obedience_jump_height",
    "Obedience jump height (inches)",
    Constants.sanctioning_club.CKC,
    "ckc_obedience_jump_height",
    ckcObedienceHeights,
  ),
  ckcRallyJumpHeight: jumpHeightField(
    "ckc_rally_jump_height",
    "Rally jump height (inches)",
    Constants.sanctioning_club.CKC,
    "ckc_rally_jump_height",
    ckcRallyHeights,
  ),
};

export const buildSavePayload = (
  ctx: RegistrationContext,
  values: Record<string, unknown>,
  savingFields: RegistrationField[],
): Record<string, unknown> => {
  const start = effectiveDog(ctx);
  let next = start;
  savingFields.forEach((field) => {
    const value = values[field.id];
    if (value === undefined || value === "") return;
    next = field.apply(next, value, ctx);
  });

  const payload: Record<string, unknown> = {};
  const scalarKeys = ["dob", "height_at_withers", "sire", "dam", "breeder", "final_response", "country_of_birth"] as const;
  scalarKeys.forEach((key) => {
    const before = start[key] instanceof Date ? (start[key] as Date).toISOString() : start[key] ?? "";
    const after = next[key] instanceof Date ? (next[key] as Date).toISOString() : next[key] ?? "";
    if (before !== after) {
      payload[key] = next[key];
    }
  });

  if (JSON.stringify(start.club_registrations ?? []) !== JSON.stringify(next.club_registrations ?? [])) {
    payload.club_registrations = next.club_registrations;
  }

  return payload;
};
